import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Snackbar,
  ThemeProvider,
  Typography,
  createTheme,
  useTheme,
} from '@mui/material';
import type { Pool } from '../types/domain';
import {
  buildCentralMonitoringPopupHtml,
  buildPoolGridEntry,
  type PoolMonitoringCctv,
} from '../components/monitoring/monitoringPopupHtml';
import { readMonitoringRotationIntervalSec } from '../components/monitoring/monitoringRotationInterval';
import {
  fetchMonitoringPlaybackRemote,
  fetchMonitoringPoolsList,
  fetchPoolCctvsForMonitoring,
} from '../components/monitoring/monitoringPopupData';
import {
  applyMonitoringPopupDocumentChrome,
  useCctvPopupCloseMessage,
} from '../components/monitoring/useCctvPopupCloseMessage';
import { useMonitoringPopupSnapshotBridge } from '../hooks/useMonitoringPopupSnapshotBridge';
import { useCentralAiOpsPresence } from '../hooks/useCentralAiOpsPresence';
import { useAuth } from '../contexts/AuthContext';
import { useEventContext } from '../contexts/EventContext';
import { API_ORIGIN } from '../config';
import PoolManualAlertDialog, {
  type PoolManualAlertTarget,
} from '../components/central-control/PoolManualAlertDialog';
import CentralRiskEventDialog from '../components/central-monitoring/CentralRiskEventDialog';
import ControlCenterSafetyAlertDialog from '../components/central-monitoring/ControlCenterSafetyAlertDialog';
import ControlRoomSirenDialog from '../components/central-monitoring/ControlRoomSirenDialog';
import {
  type CentralRiskEventPayload,
  dismissCentralRiskDialogPayload,
  isCentralRiskDialogDismissed,
  isCentralRiskUiEventDismissed,
  riskTypeToLabel,
} from '../components/central-monitoring/centralRiskEventTypes';
import { eventService } from '../services/eventService';
import { monitoringWebSocket } from '../services/websocketService';
import { isDrowningEventType } from '../services/riskClipService';
import { useMonitoringAiSettings } from '../hooks/useMonitoringAiSettings';
import { isNormalOperatingPoolStatus } from '../utils/poolUtils';
import { scheduleRiskEvidenceAtDetection } from '../utils/scheduleRiskEvidenceAtDetection';

const CctvCentralMonitoringPopupPage: React.FC = () => {
  useCctvPopupCloseMessage();
  const { authState } = useAuth();
  const opsReady =
    authState.isAuthenticated &&
    !authState.loading &&
    authState.isAdminPasswordVerified;
  useCentralAiOpsPresence(opsReady);
  const { refresh: refreshMonitoringAi } = useMonitoringAiSettings(opsReady);
  const { subscribeToEvents } = useEventContext();

  useEffect(() => {
    if (!opsReady) return;
    monitoringWebSocket.enableWebSocket();
    monitoringWebSocket.connectToControlCenter();
  }, [opsReady]);
  const [searchParams] = useSearchParams();
  const poolsPerRow = useMemo(() => {
    const fromQuery = parseInt(searchParams.get('cols') || '', 10);
    if (Number.isFinite(fromQuery) && fromQuery >= 1) return fromQuery;
    const saved = parseInt(localStorage.getItem('poolsPerRow') || '3', 10);
    return Number.isFinite(saved) && saved >= 1 ? saved : 3;
  }, [searchParams]);

  const rotationIntervalSec = useMemo(() => {
    const raw = parseInt(searchParams.get('interval') || '', 10);
    if (!Number.isFinite(raw)) return readMonitoringRotationIntervalSec();
    return Math.min(300, Math.max(1, raw));
  }, [searchParams]);

  const adminIdFilter = useMemo(() => {
    const raw = searchParams.get('adminId');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const poolIdsFilter = useMemo(() => {
    const raw = searchParams.get('poolIds');
    if (!raw) return null;
    const ids = raw
      .split(',')
      .map((s) => Math.floor(Number(s.trim())))
      .filter((id) => Number.isFinite(id) && id > 0);
    return ids.length > 0 ? new Set(ids) : null;
  }, [searchParams]);

  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operatingPoolIds, setOperatingPoolIds] = useState<number[]>([]);
  const [poolsMeta, setPoolsMeta] = useState<Pool[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [manualAlertTarget, setManualAlertTarget] = useState<PoolManualAlertTarget | null>(null);
  const [manualAlertSnack, setManualAlertSnack] = useState<string | null>(null);
  const [centralRiskEvent, setCentralRiskEvent] = useState<CentralRiskEventPayload | null>(null);
  const [centralRiskDialogOpen, setCentralRiskDialogOpen] = useState(false);
  const [outerBgLight, setOuterBgLight] = useState(() => {
    try {
      return localStorage.getItem('centralAllMonitoringBgLight') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setIframeLoaded(false);
  }, [html]);

  const emitCentralRiskEvent = useCallback((payload: CentralRiskEventPayload) => {
    if (isCentralRiskDialogDismissed(payload)) return;
    if (adminIdFilter != null) {
      const pool = poolsMeta.find((p) => Number(p.id) === Number(payload.poolId));
      const assigned = Number(
        (pool as Pool & { monitoring_admin_id?: number | null })?.monitoring_admin_id
      );
      if (Number.isFinite(assigned) && assigned !== adminIdFilter) return;
      if (operatingPoolIds.length > 0 && !operatingPoolIds.includes(Number(payload.poolId))) {
        return;
      }
    }
    const enriched = { ...payload, triggerTs: payload.triggerTs ?? Date.now() / 1000 };
    scheduleRiskEvidenceAtDetection(enriched);
    setCentralRiskEvent(enriched);
    setCentralRiskDialogOpen(true);
  }, [adminIdFilter, poolsMeta, operatingPoolIds]);

  useEffect(() => {
    return subscribeToEvents((ev) => {
      if (ev.status === 'resolved') return;
      if (isCentralRiskUiEventDismissed(ev.id)) return;
      const eventType = String(ev.metadata?.event_type || ev.title || '');
      const lower = eventType.toLowerCase();
      if (
        !isDrowningEventType(eventType) &&
        !lower.includes('risk') &&
        !lower.includes('motion') &&
        ev.type !== 'danger' &&
        ev.type !== 'warning'
      ) {
        return;
      }
      const poolId = Number(ev.poolId);
      if (!Number.isFinite(poolId)) return;
      const poolRow = poolsMeta.find((p) => String(p.id) === String(poolId));
      emitCentralRiskEvent({
        poolId,
        poolName: ev.poolName || poolRow?.name || `시설 #${poolId}`,
        cctvId:
          ev.metadata?.cctv_id != null ? Number(ev.metadata.cctv_id) : undefined,
        location: ev.location || '',
        riskType: eventType || 'drowning_risk',
        riskLabel: riskTypeToLabel(eventType),
        message: ev.description || ev.title,
        eventDbId:
          (ev.metadata?.event_db_id as number | string | undefined) ??
          eventService.resolveAckId(ev),
        uiEventId: ev.id,
        imageDataUrl: ev.imageUrl,
        safetyPhoneRaw: String(poolRow?.safety_phone || '').trim(),
        poolSirens: Array.isArray(poolRow?.sirens)
          ? poolRow.sirens.filter((s) => String(s).trim())
          : [],
        source: 'websocket',
      });
    });
  }, [subscribeToEvents, emitCentralRiskEvent, poolsMeta]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        light?: boolean;
        poolId?: number;
        poolName?: string;
        cctvId?: number;
        riskType?: string;
        riskLabel?: string;
        message?: string;
        imageDataUrl?: string;
        bboxes?: Array<{
          bbox: number[];
          frame_width?: number;
          frame_height?: number;
          trackId?: number;
          motionRisk?: boolean;
          riskSubtypeLabel?: string;
          class_name?: string;
        }>;
      } | null;
      if (!data?.type) return;
      if (data.type === 'cctv-monitoring-bg' && typeof data.light === 'boolean') {
        setOuterBgLight(data.light);
        return;
      }
      if (data.type === 'monitoring-popup-manual-alert') {
        const poolId = Number(data.poolId);
        if (!Number.isFinite(poolId) || poolId <= 0) return;
        const poolRow = poolsMeta.find((p) => Number(p.id) === poolId);
        setManualAlertTarget({
          poolId,
          poolName: data.poolName || poolRow?.name || `시설 #${poolId}`,
          safetyPhoneRaw: String(poolRow?.safety_phone || '').trim(),
        });
        return;
      }
      if (data.type === 'monitoring-popup-risk-event') {
        const poolId = Number(data.poolId);
        if (!Number.isFinite(poolId) || poolId <= 0) return;
        const poolRow = poolsMeta.find((p) => Number(p.id) === poolId);
        const riskType = String(data.riskType || 'drowning');
        const imageDataUrl =
          typeof data.imageDataUrl === 'string' && data.imageDataUrl.startsWith('data:')
            ? data.imageDataUrl
            : undefined;
        const bboxes = Array.isArray(data.bboxes)
          ? data.bboxes.filter((b) => Array.isArray(b?.bbox) && b.bbox.length >= 4)
          : undefined;
        emitCentralRiskEvent({
          poolId,
          poolName:
            (data.poolName || '').split('(')[0].trim() ||
            poolRow?.name ||
            `시설 #${poolId}`,
          cctvId: data.cctvId != null ? Number(data.cctvId) : undefined,
          location: '',
          riskType,
          riskLabel: data.riskLabel || riskTypeToLabel(riskType),
          message: data.message || '',
          imageDataUrl,
          bboxes,
          safetyPhoneRaw: String(poolRow?.safety_phone || '').trim(),
          poolSirens: Array.isArray(poolRow?.sirens)
            ? poolRow.sirens.filter((s) => String(s).trim())
            : [],
          source: 'ai',
          triggerTs: Date.now() / 1000,
        });
        return;
      }
      if (data.type === 'monitoring-popup-ai-toggled') {
        void refreshMonitoringAi();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [poolsMeta, refreshMonitoringAi, emitCentralRiskEvent]);

  useMonitoringPopupSnapshotBridge(
    iframeRef,
    Boolean(html) && iframeLoaded,
    operatingPoolIds
  );

  const outerBg = outerBgLight ? '#fff' : '#000';

  useEffect(() => {
    return applyMonitoringPopupDocumentChrome(outerBg);
  }, [outerBg]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setHtml(null);
      setError(null);
      try {
        const [useRemotePlayback, poolsRaw] = await Promise.all([
          fetchMonitoringPlaybackRemote(),
          fetchMonitoringPoolsList(),
        ]);

        if (cancelled) return;

        let pools = poolsRaw.filter((p) =>
          isNormalOperatingPoolStatus(
            p.status,
            (p as Pool & { is_active?: boolean }).is_active
          )
        );
        // 1) URL poolIds (개별 관제에서 넘긴 확정 목록) 우선
        if (poolIdsFilter != null) {
          pools = pools.filter((p) => poolIdsFilter.has(Number(p.id)));
        } else if (adminIdFilter != null) {
          // 2) 폴백: monitoring_admin_id 배당 필터
          pools = pools.filter(
            (p) =>
              Number(
                (p as Pool & { monitoring_admin_id?: number | null }).monitoring_admin_id
              ) === adminIdFilter
          );
        }

        if (pools.length === 0) {
          setError('표시할 시설 없음');
          return;
        }

        setPoolsMeta(pools);
        setOperatingPoolIds(
          pools
            .map((p) => Number(p.id))
            .filter((id) => Number.isFinite(id))
        );

        const poolGridsInfo = (
          await Promise.all(
            pools.map(async (pool: Pool) => {
              const pid = pool.id;
              let cctvs: PoolMonitoringCctv[] = [];
              if (pid != null) {
                try {
                  cctvs = await fetchPoolCctvsForMonitoring(pid);
                } catch {
                  cctvs = [];
                }
              }
              return buildPoolGridEntry(
                {
                  id: pool.id,
                  code: pool.code,
                  name: pool.name,
                  status: pool.status,
                  site_gateway_enabled: Boolean(
                    (pool as Pool & { site_gateway_enabled?: boolean }).site_gateway_enabled
                  ),
                },
                cctvs
              );
            })
          )
        ).filter((entry) => entry.cctvs.length > 0);

        if (poolGridsInfo.length === 0) {
          setError('표시할 활성 시설·CCTV가 없음');
          return;
        }

        const content = buildCentralMonitoringPopupHtml({
          title: '전체 시설 CCTV 모니터링',
          poolsPerRow,
          poolGridsInfo,
          useRemotePlayback,
          enablePoolHeaderClick: true,
          enablePoolRotationButton: true,
          enableBackgroundThemeToggle: true,
          rotationIntervalSec,
          apiOrigin: API_ORIGIN,
        });
        document.title = `all/cctv${window.location.search}`;
        setHtml(content);
      } catch (e) {
        console.error('CctvCentralMonitoringPopupPage load failed:', e);
        if (!cancelled) setError('전체 모니터링 화면을 불러오기 실패');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [poolsPerRow, rotationIntervalSec, adminIdFilter, poolIdsFilter]);

  const outerTheme = useTheme();
  const overlayTheme = useMemo(
    () =>
      createTheme(outerTheme, {
        zIndex: {
          ...outerTheme.zIndex,
          modal: 21000,
          snackbar: 21100,
        },
      }),
    [outerTheme]
  );

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#000',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Typography>{error}</Typography>
      </Box>
    );
  }

  if (!html) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress color="inherit" sx={{ color: '#888' }} />
      </Box>
    );
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        title="전체 시설 CCTV"
        srcDoc={html}
        onLoad={() => setIframeLoaded(true)}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-fullscreen"
        style={{
          border: 'none',
          display: 'block',
          width: '100vw',
          height: '100vh',
          background: outerBg,
          position: 'relative',
          zIndex: 0,
        }}
      />
      <ThemeProvider theme={overlayTheme}>
        <PoolManualAlertDialog
          open={manualAlertTarget != null}
          target={manualAlertTarget}
          onClose={() => setManualAlertTarget(null)}
          onSent={() => setManualAlertSnack('알림 메시지를 전송함')}
        />
        <CentralRiskEventDialog
          open={centralRiskDialogOpen && centralRiskEvent != null}
          payload={centralRiskEvent}
          onClose={() => {
            dismissCentralRiskDialogPayload(centralRiskEvent);
            setCentralRiskDialogOpen(false);
            setCentralRiskEvent(null);
          }}
        />
        <ControlCenterSafetyAlertDialog />
        <ControlRoomSirenDialog />
        <Snackbar
          open={Boolean(manualAlertSnack)}
          autoHideDuration={3000}
          onClose={() => setManualAlertSnack(null)}
          message={manualAlertSnack ?? ''}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </ThemeProvider>
    </>
  );
};

export default CctvCentralMonitoringPopupPage;
